import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Mail, Phone, MapPin, Send, Loader2, Users } from "lucide-react";
import { Reveal, Eyebrow } from "./shared";
import { api, apiErr, waLink } from "@/lib/api";

const slugify = (s) => (s || "").toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export const Contact = ({ settings = {}, categories = [] }) => {
  const cats = categories.length ? categories : [{ label: "Siswa", icon: "GraduationCap" }, { label: "Perusahaan", icon: "Building2" }];
  const [form, setForm] = useState({ name: "", email: "", category: cats[0]?.label || "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (categories.length && !categories.some((c) => c.label === form.category)) {
      setForm((f) => ({ ...f, category: categories[0].label }));
    }
    // eslint-disable-next-line
  }, [categories]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Mohon lengkapi semua kolom yang wajib diisi.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Pesan terkirim! Membuka WhatsApp untuk konfirmasi cepat...");
      const wa = settings.whatsapp_number;
      if (wa) {
        const text = `Halo Binara Labs! 👋\n\nNama: ${form.name}\nEmail: ${form.email}\nKategori: ${form.category}\n\nPesan:\n${form.message}`;
        window.open(waLink(wa, text), "_blank");
      }
      setForm({ name: "", email: "", category: cats[0]?.label || "", message: "" });
    } catch (err) {
      toast.error(apiErr(err) || "Gagal mengirim pesan. Silakan coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl bg-navy-soft border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan/60 focus:ring-2 focus:ring-cyan/20 transition-all";

  const mapSrc = (() => {
    const m = settings.map_embed;
    if (m && /^https?:\/\//i.test(m)) return m;
    const q = m || settings.contact_address || "Jakarta, Indonesia";
    return `https://www.google.com/maps?q=${encodeURIComponent(q)}&output=embed`;
  })();

  const selectedCat = cats.find((c) => c.label === form.category);

  const info = [
    { icon: MapPin, label: "Alamat", value: settings.contact_address },
    { icon: Mail, label: "Email", value: settings.contact_email },
    { icon: Phone, label: "Telepon", value: settings.contact_phone },
  ].filter((i) => i.value);

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div>
            <Reveal>
              <Eyebrow testid="contact-eyebrow">Hubungi Kami</Eyebrow>
              <h2 className="mt-6 font-heading font-bold text-3xl md:text-5xl tracking-tight leading-tight">
                Mari wujudkan <span className="text-gradient-gold">prestasi</span> berikutnya.
              </h2>
              <p className="mt-5 text-slate-400 leading-relaxed max-w-md">
                Baik untuk pembinaan siswa maupun kerja sama korporat, tim kami siap merancang solusi yang tepat untuk Anda.
                Pengajuan Anda langsung diteruskan ke WhatsApp tim kami untuk konfirmasi cepat.
              </p>
            </Reveal>

            <div className="mt-10 space-y-5">
              {info.map((c) => (
                <Reveal key={c.label} delay={0.1}>
                  <div className="flex items-start gap-4" data-testid={`contact-info-${c.label.toLowerCase()}`}>
                    <span className="grid place-items-center w-11 h-11 rounded-xl bg-navy-card border border-white/10 text-cyan shrink-0">
                      <c.icon className="w-5 h-5" />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-slate-500">{c.label}</p>
                      <p className="text-slate-200 mt-0.5">{c.value}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.15}>
              <div className="mt-8 relative overflow-hidden rounded-2xl border border-white/10 h-64">
                <iframe
                  title="Lokasi Binara Labs"
                  data-testid="contact-map"
                  src={mapSrc}
                  className="w-full h-full"
                  style={{ border: 0 }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-white/10 bg-navy-card/50 p-7 md:p-9 relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-48 h-48 radial-glow-gold" />
              <div className="relative z-10 space-y-5">
                <div>
                  <label className="text-sm text-slate-300 font-medium">Nama Lengkap</label>
                  <input data-testid="contact-name" className={`mt-2 ${inputCls}`} value={form.name} onChange={update("name")} placeholder="Nama Anda" />
                </div>
                <div>
                  <label className="text-sm text-slate-300 font-medium">Email</label>
                  <input data-testid="contact-email" type="email" className={`mt-2 ${inputCls}`} value={form.email} onChange={update("email")} placeholder="email@contoh.com" />
                </div>
                <div>
                  <label className="text-sm text-slate-300 font-medium">Kategori</label>
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    {cats.map((o) => {
                      const Icon = Icons[o.icon] || Users;
                      const active = form.category === o.label;
                      return (
                        <button
                          key={o.id || o.label}
                          type="button"
                          data-testid={`contact-category-${slugify(o.label)}`}
                          onClick={() => setForm((f) => ({ ...f, category: o.label }))}
                          className={`flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold transition-all ${
                            active ? "border-cyan bg-cyan/10 text-cyan" : "border-white/10 text-slate-400 hover:border-white/25"
                          }`}
                        >
                          <Icon className="w-4 h-4" /> {o.label}
                        </button>
                      );
                    })}
                  </div>
                  {selectedCat?.desc && (
                    <p key={selectedCat.label} className="mt-2.5 text-xs text-slate-400 leading-relaxed border-l-2 border-cyan/40 pl-3" data-testid="contact-category-desc">
                      {selectedCat.desc}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-300 font-medium">Pesan</label>
                  <textarea data-testid="contact-message" rows={4} className={`mt-2 resize-none ${inputCls}`} value={form.message} onChange={update("message")} placeholder="Ceritakan kebutuhan Anda..." />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3.5 font-body font-bold text-navy hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] disabled:opacity-60 transition-all duration-300"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  {loading ? "Mengirim..." : "Kirim & Chat via WhatsApp"}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
